import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Share2, Check, Link as LinkIcon, Coffee } from 'lucide-react';
import TakeABreakOverlay from '../Overlays/TakeABreakOverlay';

// 6-step methodology, mapped onto 3 view modes:
//   Step 1: Capture (brainstorm)
//   Steps 2-5: Build (Find Big Idea, They Must Understand, Align, Prioritize)
//   Step 6: Map (deliverable)
const VIEW_MODES = [
  { key: 'brainstorm', label: '1 · Capture' },
  { key: 'organize', label: '2-5 · Build' },
  { key: 'list', label: '6 · Map' },
];

export default function CanvasHeader({
  mapName,
  clientName,
  versionCount,
  versions = [],
  onUpdateName,
  onSave,
  onRestoreVersion,
  saving,
  viewMode,
  onSetViewMode,
  tabsDisabled = false,
  onShare,
  shareUrl,
  measurement,
  onUpdateMeasurement,
}) {
  const [copied, setCopied] = useState(false);
  const [showBreak, setShowBreak] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(false);
  const [measurementText, setMeasurementText] = useState(measurement || '');
  const measurementRef = useRef(null);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(mapName);
  const [showVersions, setShowVersions] = useState(false);
  const inputRef = useRef(null);
  const versionRef = useRef(null);

  useEffect(() => { setName(mapName); }, [mapName]);
  useEffect(() => { setMeasurementText(measurement || ''); }, [measurement]);
  useEffect(() => {
    if (editingMeasurement && measurementRef.current) measurementRef.current.focus();
  }, [editingMeasurement]);

  function saveMeasurement() {
    setEditingMeasurement(false);
    const trimmed = measurementText.trim();
    if (trimmed !== (measurement || '')) onUpdateMeasurement(trimmed);
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Close version dropdown on outside click
  useEffect(() => {
    if (!showVersions) return;
    function handleClick(e) {
      if (versionRef.current && !versionRef.current.contains(e.target)) {
        setShowVersions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showVersions]);

  function saveName() {
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== mapName) onUpdateName(trimmed);
  }

  function formatVersionDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  }

  return (
    <div className="flex-shrink-0" data-no-print>
    <div className="bg-white px-5 py-2 flex items-center gap-3">
      <button
        onClick={() => navigate('/dashboard')}
        className="text-txt-secondary hover:text-txt-primary transition-colors"
        title="Back to Dashboard"
      >
        &larr;
      </button>

      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveName();
            if (e.key === 'Escape') { setEditing(false); setName(mapName); }
          }}
          className="text-lg font-bold border-b-2 border-gold outline-none
            bg-transparent px-0 py-0 focus:ring-0"
        />
      ) : (
        <h1
          className="text-lg font-heading font-semibold text-txt-primary cursor-pointer hover:text-black transition-colors"
          onClick={() => setEditing(true)}
          title="Click to edit name"
        >
          {mapName}
        </h1>
      )}

      <span className="bg-gold/10 text-gold text-xs font-medium px-2.5 py-1 rounded-full">
        {clientName}
      </span>

      <div className="flex-1" />

      {/* Version indicator + dropdown */}
      {versionCount > 0 && (
        <div className="relative" ref={versionRef}>
          <button
            onClick={() => setShowVersions(!showVersions)}
            className="text-xs text-txt-secondary bg-scaffold px-2.5 py-1 rounded flex items-center gap-1 hover:bg-gray-200 transition-colors"
          >
            v{versionCount}
            <ChevronDown className="w-3 h-3" />
          </button>

          {showVersions && versions.length > 0 && (
            <div className="absolute top-full right-0 mt-1 z-50 bg-white rounded-lg shadow-xl border border-navy/10 py-1 w-56 max-h-64 overflow-auto">
              <p className="px-3 py-1.5 text-[10px] font-heading font-semibold text-txt-secondary uppercase">
                Version History
              </p>
              {versions.slice().reverse().map((v) => (
                <button
                  key={v.id}
                  onClick={() => {
                    if (window.confirm(`Restore to version ${v.versionNumber}? Current work will be saved first.`)) {
                      onRestoreVersion(v.id);
                      setShowVersions(false);
                    }
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span className="text-sm text-txt-primary">v{v.versionNumber}</span>
                  <span className="text-xs text-txt-secondary">{formatVersionDate(v.createdAt)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Take a Break */}
      <button
        onClick={() => setShowBreak(true)}
        className="flex items-center gap-1.5 text-sm text-txt-secondary hover:text-teal-dark
          border border-teal/40 bg-teal/10 hover:bg-teal/20 px-3 py-1.5 rounded-lg transition-colors"
        title="Take a Break"
      >
        <Coffee className="w-3.5 h-3.5" />
        Take a Break
      </button>

      {/* View mode toggle */}
      <div className="flex items-center border border-black/10 rounded-lg overflow-hidden text-sm font-body">
        {VIEW_MODES.map((mode, i) => (
          <button
            key={mode.key}
            onClick={() => !tabsDisabled && onSetViewMode(mode.key)}
            disabled={tabsDisabled && mode.key !== viewMode}
            className={`px-3.5 py-1.5 transition-colors ${
              i > 0 ? 'border-l border-black/10' : ''
            } ${
              viewMode === mode.key
                ? 'bg-navy text-white'
                : tabsDisabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-txt-secondary hover:text-txt-primary hover:bg-gray-50'
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {/* Share button — only on Map view */}
      {viewMode === 'list' && onShare && (
        shareUrl ? (
          <button
            onClick={() => {
              navigator.clipboard.writeText(shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="flex items-center gap-1.5 text-sm text-txt-secondary hover:text-txt-primary
              border border-navy/10 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            title="Copy share link"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-brand-green" /> : <LinkIcon className="w-3.5 h-3.5" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        ) : (
          <button
            onClick={onShare}
            className="flex items-center gap-1.5 text-sm text-txt-secondary hover:text-txt-primary
              border border-navy/10 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            title="Create share link"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        )
      )}

      <button
        onClick={onSave}
        disabled={saving}
        className="btn-gold text-sm disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Version'}
      </button>
    </div>

    {/* Measurement bar — hidden in Map view (the Report/List views are self-contained) */}
    <div className={`bg-gold/25 border-b-2 border-gold-dark px-5 py-2 shadow-sm flex items-baseline justify-center gap-3 ${viewMode === 'list' ? 'hidden' : ''}`}>
      <span className="text-xs font-heading font-bold text-gold-dark uppercase tracking-widest whitespace-nowrap flex-shrink-0">
        What Does Success Look Like?
      </span>
      {editingMeasurement ? (
        <input
          ref={measurementRef}
          type="text"
          value={measurementText}
          maxLength={80}
          onChange={(e) => setMeasurementText(e.target.value)}
          onBlur={saveMeasurement}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveMeasurement();
            if (e.key === 'Escape') { setEditingMeasurement(false); setMeasurementText(measurement || ''); }
          }}
          className="text-sm font-semibold border-b-2 border-gold-dark outline-none bg-transparent px-0 py-0 focus:ring-0 w-96 text-center"
          placeholder="Click to set your measurement..."
        />
      ) : (
        <span
          className="text-sm font-semibold text-txt-primary cursor-pointer hover:text-navy transition-colors truncate"
          onClick={() => setEditingMeasurement(true)}
          title={measurement || 'Click to edit'}
        >
          {measurement || 'Click to set your measurement...'}
        </span>
      )}
    </div>

    {/* Break overlay */}
    {showBreak && <TakeABreakOverlay onClose={() => setShowBreak(false)} />}
    </div>
  );
}
